import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useSignals } from "@/lib/hooks";
import { cn, scoreColor, scoreBgClass } from "@/lib/utils";
import { SignalBadge } from "@/components/ui/Badge";
import {
  Search,
  LayoutDashboard,
  Signal,
  Map,
  FlaskConical,
  BarChart3,
  TrendingUp,
  Wallet,
  FileText,
  Settings,
  Terminal,
  Sunrise,
  Star,
  Activity,
} from "lucide-react";

const PAGES = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/brief", label: "Morning Brief", icon: Sunrise },
  { path: "/signals", label: "Signals", icon: Signal },
  { path: "/portfolio", label: "Portfolio", icon: Wallet },
  { path: "/watchlist", label: "Watchlist", icon: Star },
  { path: "/research", label: "Research", icon: FlaskConical },
  { path: "/options", label: "Options", icon: Activity },
  { path: "/backtests", label: "Backtests", icon: BarChart3 },
  { path: "/market", label: "Market Map", icon: Map },
  { path: "/macro", label: "Macro", icon: TrendingUp },
  { path: "/reports", label: "Reports", icon: FileText },
  { path: "/system", label: "System", icon: Settings },
  { path: "/dev", label: "Console", icon: Terminal },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

type ResultItem =
  | { type: "page"; path: string; label: string; icon: typeof Search }
  | { type: "ticker"; ticker: string; signal: string | null; score: number | null };

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Fetch signals for ticker search (cached by React Query)
  const signals = useSignals({ pageSize: "500" });
  const signalData = signals.data?.data ?? [];

  // Build results
  const results = useMemo<ResultItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PAGES.slice(0, 6).map((p) => ({ type: "page" as const, ...p }));

    const items: ResultItem[] = [];

    // Search pages
    for (const page of PAGES) {
      if (page.label.toLowerCase().includes(q) || page.path.toLowerCase().includes(q)) {
        items.push({ type: "page", ...page });
      }
    }

    // Search tickers
    for (const s of signalData) {
      const ticker = s.ticker as string;
      if (ticker?.toLowerCase().startsWith(q) || ticker?.toLowerCase().includes(q)) {
        items.push({
          type: "ticker",
          ticker,
          signal: (s.signal as string) ?? null,
          score: s.composite_score != null ? Number(s.composite_score) : null,
        });
      }
      if (items.length >= 12) break;
    }

    return items;
  }, [query, signalData]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Reset selected when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results.length]);

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const select = useCallback(
    (item: ResultItem) => {
      onClose();
      if (item.type === "page") {
        navigate(item.path);
      } else {
        navigate(`/research/${item.ticker}`);
      }
    },
    [navigate, onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (results[selectedIndex]) select(results[selectedIndex]);
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    },
    [results, selectedIndex, select, onClose],
  );

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="fixed inset-x-4 top-[15vh] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[520px] z-[61] animate-fade-in">
        <div className="bg-[var(--color-surface-1)] rounded-xl shadow-2xl shadow-black/40 overflow-hidden border border-white/[0.06]">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-white/[0.06]">
            <Search size={18} className="text-zinc-500 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search tickers, pages..."
              className="flex-1 bg-transparent py-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
            />
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/[0.06] text-[10px] font-mono text-zinc-500">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1">
            {results.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-zinc-600">
                No results for &ldquo;{query}&rdquo;
              </div>
            )}

            {results.map((item, i) => (
              <button
                key={item.type === "page" ? item.path : item.ticker}
                onClick={() => select(item)}
                onMouseEnter={() => setSelectedIndex(i)}
                className={cn(
                  "flex items-center gap-3 w-full px-4 py-2.5 text-left text-sm transition-colors",
                  i === selectedIndex
                    ? "bg-white/[0.06] text-zinc-100"
                    : "text-zinc-400 hover:bg-white/[0.03]",
                )}
              >
                {item.type === "page" ? (
                  <>
                    <item.icon size={16} className={cn("shrink-0", i === selectedIndex && "text-amber-400")} />
                    <span className="font-medium">{item.label}</span>
                    <span className="ml-auto text-[11px] text-zinc-600 font-mono">{item.path}</span>
                  </>
                ) : (
                  <>
                    <span className="w-4 h-4 rounded bg-amber-500/10 flex items-center justify-center text-[9px] font-bold text-amber-400 shrink-0">$</span>
                    <span className="font-mono font-semibold text-zinc-100">{item.ticker}</span>
                    {item.signal && <SignalBadge signal={item.signal} />}
                    {item.score != null && (
                      <span className={cn("ml-auto font-mono text-xs font-bold px-1.5 py-0.5 rounded", scoreColor(item.score), scoreBgClass(item.score))}>
                        {item.score.toFixed(1)}
                      </span>
                    )}
                    {item.score == null && (
                      <span className="ml-auto text-zinc-600 text-xs">&mdash;</span>
                    )}
                  </>
                )}
              </button>
            ))}
          </div>

          {/* Footer hint */}
          <div className="flex items-center gap-4 px-4 py-2 border-t border-white/[0.06] text-[10px] text-zinc-600">
            <span><kbd className="font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono">↵</kbd> select</span>
            <span><kbd className="font-mono">esc</kbd> close</span>
          </div>
        </div>
      </div>
    </>
  );
}
